import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import FileIcon from "@mui/icons-material/Collections";
import { Button, Modal, Typography } from "@mui/material";
import { red } from "@mui/material/colors";
import { styled } from "@mui/material/styles";
import { useState } from "react";
import Dropzone from "react-dropzone";
import operations from "./ducks/operations.js";

const StyledHeader = styled("h4")({
    marginTop: "45px",
    textAlign: "left",
    fontSize: "18px",
    marginLeft: "24px",
});

const StyledContainer = styled("div")(({ theme }) => ({
    margin: "0 auto",
    marginTop: "10%",
    position: "relative",
    alignItems: "center",
    borderRadius: "3px",
    "&:focus": {
        outline: "none",
    },
    width: "700px",
    height: "635px",
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
}));

const StyledButton = styled(Button)({
    display: "inline",
    color: red[500],
    "&:hover": {
        backgroundColor: red[100],
    },
    border: "1px solid",
    borderColor: red[500],
});

const StyledIcon = styled(CloudUploadIcon)({
    color: "#9E9E9E",
    verticalAlign: "middle",
    width: "20px",
    height: "20px",
});

const StyledFileIcon = styled(FileIcon)({
    color: "#9E9E9E",
    verticalAlign: "middle",
    width: "20px",
    height: "20px",
});

const StyledLink = styled("a")({
    margin: 0,
    padding: 0,
    color: "black",
    textDecoration: "underline",
    fontWeight: "bold",
    cursor: "pointer",
    "&:hover": {
        backgroundColor: "rgba(44, 153, 210, 0.0)",
        color: "rgb(44, 153, 210)",
        textDecoration: "underline",
        cursor: "pointer",
    },
});

const ListingImportModal = (props) => {
    const { closeModal, importResponse, modalOpen } = props;

    const [selectedFile, setSelectedFile] = useState(null);
    const [selectedFileName, setSelectedFileName] = useState("");
    const [fileRejected, setFileRejected] = useState(false);
    const [fileReady, setFileReady] = useState(false);
    const [templateLink] = useState(
        "https://s3.us-east-2.amazonaws.com/connect.kidder.com/content/KMC_ListingImport_StandardTemplate.csv"
    );

    const onFileChange = (file) => {
        try {
            setSelectedFile(file[0]);
            setSelectedFileName(file[0].name);
            setFileRejected(false);
            setFileReady(true);
        } catch (e) {
            setFileRejected(true);
            setFileReady(false);
            setSelectedFileName("");
        }
    };

    const onFileRemove = () => {
        setSelectedFile(null);
        setSelectedFileName("");
        setFileRejected(false);
        setFileReady(false);
    };

    const onFileSubmit = async () => {
        setFileReady(false); // Stops repeated button press after first submit
        const status = await operations.uploadListingFile(selectedFile);
        if (status >= 200 && status < 300) {
            importResponse(true);
        } else {
            importResponse(false);
        }
        onFileRemove();
        closeModal();
    };

    const onExitModal = () => {
        onFileRemove();
        closeModal();
    };

    const trimFileName = () => {
        let stringLength = selectedFileName.length;
        if (stringLength > 40) {
            return selectedFileName
                .substring(0, 31)
                .concat("...", selectedFileName.substring(stringLength - 7, stringLength));
        } else {
            return selectedFileName;
        }
    };

    return (
        <Modal
            open={modalOpen}
            onClose={() => onExitModal()}
            style={{
                height: "60vh",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                marginTop: "10vh",
            }}
        >
            <StyledContainer>
                <StyledHeader>Import Listings</StyledHeader>
                <Typography variant="body1" style={{ marginRight: "24px", marginLeft: "24px" }}>
                    Use the file selection tool to upload a CSV file containing outside listing data. Please ensure your
                    column names exactly match the provided{" "}
                    <StyledLink href={templateLink} download>
                        Standard Import Template
                    </StyledLink>
                    .
                </Typography>
                <Typography variant="body1" style={{ marginRight: "24px", marginLeft: "24px", marginTop: "24px" }}>
                    Once your file is uploaded, you will receive an email summary within 30 minutes.
                </Typography>
                <Dropzone
                    accept={[
                        ".csv",
                        "text/csv",
                        "application/csv",
                        "text/x-csv",
                        "application/x-csv",
                        "text/comma-separated-values",
                        "text/x-comma-separated-values",
                    ]}
                    onDrop={(acceptedFiles) => onFileChange(acceptedFiles)}
                    style={{
                        width: "560px",
                        textAlign: "center",
                        paddingTop: "64px",
                        paddingBottom: "64px",
                        margin: "0 auto",
                        marginTop: "25px",
                        marginBottom: "25px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        borderWidth: 2,
                        borderRadius: 5,
                        borderColor: "#9E9E9E",
                        borderStyle: "dashed",
                    }}
                >
                    <div className="dragdrop-instructions">
                        <StyledIcon />
                        <h4>Drop files in this area to attach or browse here</h4>
                        <p> (.csv files only)</p>
                    </div>
                </Dropzone>
                {fileRejected === false ? (
                    <div>
                        {selectedFileName !== "" ? (
                            <Typography variant="body1" style={{ marginLeft: "24px" }}>
                                Selected file: <StyledFileIcon />
                                <p style={{ color: "#2C99D2", display: "inline", marginLeft: "0px" }}>
                                    {trimFileName()}
                                </p>
                            </Typography>
                        ) : (
                            <Typography variant="body1" style={{ marginLeft: "24px" }}>
                                No File Selected
                            </Typography>
                        )}
                    </div>
                ) : (
                    <div>
                        <Typography variant="body1" style={{ marginLeft: "24px", color: red[500] }}>
                            Failed: File must be of type CSV.
                        </Typography>
                    </div>
                )}
                <div style={{ position: "absolute", bottom: 24, right: 24 }}>
                    {selectedFileName !== "" && (
                        <StyledButton variant="outlined" onClick={onFileRemove}>
                            Remove File
                        </StyledButton>
                    )}
                    <Button
                        style={{ display: "inline-block", marginLeft: "10px" }}
                        variant="outlined"
                        onClick={onExitModal}
                    >
                        Exit
                    </Button>
                    <Button
                        style={{ display: "inline-block", marginLeft: "10px" }}
                        variant="contained"
                        color="primary"
                        disabled={fileReady ? false : true}
                        onClick={onFileSubmit}
                    >
                        Submit
                    </Button>
                </div>
            </StyledContainer>
        </Modal>
    );
};

export default ListingImportModal;
